import React, { useEffect, useRef, useState } from 'react';
import type { EmAttachment, EmThread } from '../../email/types';
import {
  emIsNonEnglish,
  emLangLabel,
  threadHasStoredTranslation,
  threadNeedsTranslation,
} from '../../email/utils';
import { Icon } from '../Icon/Icon';
import { IconButton } from '../IconButton/IconButton';
import { EmailAuditModal } from './EmailAuditModal';
import { EmailInlineComposer } from './EmailInlineComposer';
import type { EmailReplySendPayload } from './EmailInlineComposer';
import { EmailMessageRow } from './EmailMessageRow';
import { EmailSeenBy } from './EmailSeenBy';

type ReplyMode = 'reply' | 'replyAll' | 'forward';

export interface EmailThreadProps {
  thread: EmThread;
  intelOpen: boolean;
  onToggleIntel: () => void;
  onOpenAttachment?: ((attachment: EmAttachment, messageId: string) => void) | undefined;
  onDownloadAttachment?: ((attachment: EmAttachment, messageId: string) => void) | undefined;
  loadAttachmentPreview?: ((attachment: EmAttachment, messageId: string) => Promise<string | null>) | undefined;
  onSendReply?: (payload: EmailReplySendPayload) => void;
  sendingReply?: boolean;
  onTranslate?: (options?: { force?: boolean }) => void;
  translating?: boolean;
}

export function EmailThread({
  thread,
  intelOpen,
  onToggleIntel,
  onOpenAttachment,
  onDownloadAttachment,
  loadAttachmentPreview,
  onSendReply,
  sendingReply = false,
  onTranslate,
  translating = false,
}: EmailThreadProps) {
  const [auditOpen, setAuditOpen] = useState(false);
  const intelCount = thread.suggested.length + (thread.triage ? 1 : 0);
  const msgs = thread.messages;
  const last = msgs[msgs.length - 1];
  const lastId = last?.id ?? '';
  const lastFromPatient = last?.who === 'patient';
  const sourceLang = thread.detectedLang ?? thread.lang;
  const canTranslate = threadNeedsTranslation(thread);
  const hasStoredTranslation = threadHasStoredTranslation(thread);
  const showThreadSeenBy = Boolean(thread.seenBy?.length || thread.seenCount);

  const [openMap, setOpenMap] = useState<Record<string, boolean>>({});
  const [composerOpen, setComposerOpen] = useState(false);
  const [mode, setMode] = useState<ReplyMode>('reply');
  const [showTranslatedView, setShowTranslatedView] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (hasStoredTranslation) setShowTranslatedView(true);
  }, [thread.id, hasStoredTranslation]);

  useEffect(() => {
    if (!translating && hasStoredTranslation) setShowTranslatedView(true);
  }, [translating, hasStoredTranslation]);

  useEffect(() => {
    if (!lastId) {
      setOpenMap({});
      setComposerOpen(false);
      setMode('reply');
      if (scrollRef.current) scrollRef.current.scrollTop = 0;
      return;
    }
    setOpenMap({ [lastId]: true });
    setComposerOpen(lastFromPatient);
    setMode('reply');
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [thread.id, lastId, lastFromPatient]);

  const toggle = (id: string) => setOpenMap((m) => ({ ...m, [id]: !m[id] }));
  const startReply = (mo: ReplyMode) => {
    setMode(mo);
    setComposerOpen(true);
  };
  const draft = lastFromPatient && thread.draft ? thread.draft : null;
  const np = thread.subjectTr || thread.subject;

  return (
    <div
      className="flex min-h-0 flex-1 flex-col bg-[var(--cream-0)]"
      style={{ minWidth: 420 }}
    >
      <div className="flex min-h-0 flex-1" style={{ padding: '14px 16px' }}>
        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-[var(--r-lg)] border border-[var(--cream-2)] bg-[var(--cream-1)] shadow-[var(--sh-1)]">
          <div className="h-[3px] shrink-0 bg-[var(--grad-spectrum)]" />
          <div
            className="flex shrink-0 flex-wrap items-start border-b border-[var(--cream-2)]"
            style={{
              padding: '15px 18px',
              gap: 14,
              background:
                'linear-gradient(118deg, color-mix(in srgb, var(--ai-3) 11%, var(--cream-1)), color-mix(in srgb, var(--ai-5) 9%, var(--cream-1)))',
            }}
          >
            <div style={{ minWidth: 200, flex: 1 }}>
              <h2
                style={{
                  margin: 0,
                  fontFamily: 'var(--font-display)',
                  fontSize: 19,
                  fontWeight: 600,
                  letterSpacing: '-0.02em',
                  lineHeight: 1.2,
                  color: 'var(--ink-0)',
                }}
              >
                {np}
              </h2>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 7,
                  marginTop: 5,
                  fontSize: 12.5,
                  color: 'var(--ink-2)',
                  flexWrap: 'wrap',
                }}
              >
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                  <Icon
                    kind={thread.verified === 'patient' ? 'shield-check' : 'shield-off'}
                    size={12}
                    style={{ color: thread.verified === 'patient' ? 'var(--success)' : 'var(--ink-4)' }}
                  />
                  <b style={{ color: 'var(--ink-1)' }}>{thread.name}</b>
                </span>
                <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--ink-3)' }}>{thread.email}</span>
                <span style={{ color: 'var(--ink-4)' }}>·</span>
                <span style={{ color: 'var(--ink-3)' }}>
                  {msgs.length} message{msgs.length > 1 ? 's' : ''}
                </span>
                {emIsNonEnglish(sourceLang) ? (
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: 'var(--gold)',
                      background: 'var(--gold-tint)',
                      padding: '1px 6px',
                      borderRadius: 4,
                    }}
                  >
                    {emLangLabel(sourceLang).slice(0, 2).toUpperCase()} → EN
                  </span>
                ) : null}
              </div>
              {showThreadSeenBy ? (
                <div style={{ marginTop: 8, maxWidth: 420 }}>
                  <EmailSeenBy
                    receipts={thread.seenBy ?? []}
                    {...(thread.seenCount != null ? { seenCount: thread.seenCount } : {})}
                  />
                </div>
              ) : null}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              {canTranslate && onTranslate ? (
                <>
                  <button
                    type="button"
                    className="em-act"
                    disabled={translating}
                    onClick={() => {
                      onTranslate(hasStoredTranslation ? { force: true } : undefined);
                    }}
                    title={hasStoredTranslation ? 'Re-translate thread' : 'Translate inbound messages to English'}
                  >
                    <Icon kind="translate" size={13} />
                    {translating ? 'Translating…' : hasStoredTranslation ? 'Re-translate' : 'Translate'}
                  </button>
                  {hasStoredTranslation ? (
                    <button
                      type="button"
                      className={`em-act ${showTranslatedView ? 'active' : ''}`}
                      onClick={() => setShowTranslatedView((value) => !value)}
                      aria-pressed={showTranslatedView}
                    >
                      {showTranslatedView ? 'Show original' : 'Show translation'}
                    </button>
                  ) : null}
                </>
              ) : null}
              <button
                type="button"
                className={`em-intel-btn ${intelOpen ? 'active' : ''}`}
                onClick={onToggleIntel}
                aria-pressed={intelOpen}
                title="AI intelligence — patient context, suggested actions & history"
              >
                <Icon kind="sparkle" size={14} stroke={2} />
                AI intelligence
                {intelCount > 0 ? <span className="em-intel-count">{intelCount}</span> : null}
              </button>
              <IconButton icon="clock" size={34} title="Audit log" onClick={() => setAuditOpen(true)} />
              <IconButton icon="trash" size={34} title="Archive" />
            </div>
          </div>

          <div
            ref={scrollRef}
            className="cal-scroll flex min-h-0 flex-1 flex-col overflow-y-auto"
            style={{ padding: '16px 18px', gap: 10 }}
          >
            {msgs.length === 0 ? (
              <div className="rounded-[var(--r-md)] border border-dashed border-[var(--cream-2)] bg-[var(--cream-0)] px-4 py-8 text-center text-[13px] text-[var(--ink-3)]">
                No messages in this thread yet.
              </div>
            ) : (
              msgs.map((m) => (
                <EmailMessageRow
                  key={m.id}
                  thread={thread}
                  message={m}
                  open={!!openMap[m.id]}
                  showTranslated={showTranslatedView}
                  onToggle={() => toggle(m.id)}
                  onReply={startReply}
                  onOpenAttachment={onOpenAttachment}
                  onDownloadAttachment={onDownloadAttachment}
                  loadAttachmentPreview={loadAttachmentPreview}
                />
              ))
            )}

            {msgs.length > 0 && composerOpen ? (
              <EmailInlineComposer
                thread={thread}
                mode={mode}
                setMode={setMode}
                draft={draft}
                onClose={() => setComposerOpen(false)}
                {...(onSendReply ? { onSend: onSendReply } : {})}
                sending={sendingReply}
              />
            ) : msgs.length > 0 ? (
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" className="em-act" onClick={() => startReply('reply')}>
                  <Icon kind="undo" size={13} />
                  Reply
                </button>
                <button type="button" className="em-act" onClick={() => startReply('replyAll')}>
                  <Icon kind="team" size={13} />
                  Reply all
                </button>
                <button type="button" className="em-act" onClick={() => startReply('forward')}>
                  <Icon kind="arrow-r" size={13} />
                  Forward
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <EmailAuditModal thread={thread} open={auditOpen} onClose={() => setAuditOpen(false)} />
    </div>
  );
}
